import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Collapse } from 'antd';

import { cleanString, createMarkup } from '../../util/renderHelper';
import { styleSafeString } from '../../util/pathSafeString';

import { ReactComponent as IconFile } from '../Sprites/icon-file-download.svg';
import { ReactComponent as IconFileDOC } from '../Sprites/icon-file-word.svg';
import { ReactComponent as IconFilePDF } from '../Sprites/icon-file-pdf.svg';
import { ReactComponent as IconFilePPT } from '../Sprites/icon-file-powerpoint.svg';
import { ReactComponent as IconFileXLS } from '../Sprites/icon-file-excel.svg';

import * as actions from '../../actions';

const { Panel } = Collapse;

const getFileIcon = fileExtension => {
  switch (fileExtension) {
    case 'doc':
    case 'dot':
    case 'docx':
    case 'docm':
    case 'dotx':
    case 'dotm':
      return <IconFileDOC className="resource-item__icon" />;
    case 'pdf':
      return <IconFilePDF className="resource-item__icon" />;
    case 'ppt':
    case 'pot':
    case 'pps':
    case 'pptx':
    case 'potx':
    case 'ppsx':
    case 'pptm':
    case 'potm':
    case 'ppsm':
      return <IconFilePPT className="resource-item__icon" />;
    case 'xls':
    case 'xlt':
    case 'xlsx':
    case 'xlsm':
    case 'xltx':
    case 'xltm':
      return <IconFileXLS className="resource-item__icon" />;
    default:
      return <IconFile className="resource-item__icon" />;
  }
};

const getFileExtension = fileName => fileName.split('.').pop();

const remapFile = (file) => {
  return {
    id: file.asset_assetid,
    fileName: file.asset_attribute_name,
    fileTitle: file.asset_attribute_title,
    fileSize: file.asset_file_size_readable,
    type: file.asset_type,
    typeCode: file.asset_type_code,
    url: file.asset_url,
  }
};

const ResourceListCollapse = (props) => {
  const {
    file,
    location,
    selfExpandIndex,
  } = props;
  const {
    contents,
    fileName,
    files,
    url,
  } = file;

  const folderUrlParts = new URL(url);
  const folderUrlPathSegments = folderUrlParts.pathname.split('/');
  const slug = folderUrlPathSegments[folderUrlPathSegments.length - 1];

  const urlParams = new URLSearchParams(location.search);
  /*
   * URLSearchParams.get('drop') returns the first
   * "drop" param value:
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/get
   *
   * For the sake of robustness and predictability,
   * we are deliberately not using URLSearchParams.getAll('drop')
   */
  const urlAutoExpandIndex = parseInt(urlParams.get('drop'), 10);

  /*
   * ResourceList applies a 1-indexed selfExpandIndex to each
   * ResourceListCollapse item:
   *
   * <ResourceList>
   *   <ResourceListCollapse selfExpandIndex={1} />
   *   <ResourceListCollapse selfExpandIndex={2} />
   *   <ResourceListCollapse selfExpandIndex={3} />
   *   <ResourceItem />
   * </ResourceList>
   *
   * Each ResourceListCollapse is an Ant Collapse component
   * containing exactly one Panel. If selfExpandIndex matches
   * URL search param "drop", the defaultActiveKey is always "1":
   *
   * https://tac-stage-www.clients.squiz.net/providers/type/occupational-therapist/treating-a-client?drop=2&drop=3#provider-tabs
   *
   * <ResourceList>
   *   <ResourceListCollapse selfExpandIndex={1} />
   *   <ResourceListCollapse selfExpandIndex={2}>
   *     <Collapse defaultActiveKey={["1"]}>
   *       <Panel key="1" />
   *     </Collapse>
   *   </ResourceListCollapse>
   *   <ResourceListCollapse selfExpandIndex={3} />
   *   <ResourceItem />
   * </ResourceList>
   *
   */
  const collapseProps = {};
  if (!!urlAutoExpandIndex && selfExpandIndex === urlAutoExpandIndex) {
    collapseProps.defaultActiveKey = ['1'];
  }

  return (
    <Collapse className={styleSafeString(slug)} {...collapseProps}>
      <Panel header={cleanString(fileName)} key="1">
        {contents ? (
        <div dangerouslySetInnerHTML={createMarkup(cleanString(contents))} />
        ):( null)}
        {
          files.map((item, key) => {
            return <ResourceItem key={key} file={remapFile(item)} />
          })
        }
      </Panel>
    </Collapse>
  );
};

const ResourceItem = ({ file }) => {
  if (file.typeCode.includes("page_")) {
    return <ResourceItemPage file={file} />;
  } else {
    return (
      <a
        href={cleanString(file.url)}
        alt={`Download ${cleanString(file.fileTitle)}`}
        className="resource-item"
        aria-label={`Downloads ${cleanString(file.fileTitle)}`}
      >
        {getFileIcon(getFileExtension(cleanString(file.fileName)))}
        <span className="resource-item__text">
          <strong>Download file:</strong>{" "}
          {`${cleanString(file.fileTitle)} ( .${getFileExtension(
            cleanString(file.fileName)
          )} - ${cleanString(file.fileSize)})`}
        </span>
      </a>
    );
  }
};

const ResourceItemPage = ({ file }) => {
  return (
    <a
      href={cleanString(file.url)}
      target="_blank"
      rel="noopener noreferrer"
      alt={`Download ${cleanString(file.fileName)}`}
      className="resource-item"
      aria-label={`Downloads ${cleanString(file.fileName)}`}
    >
      <IconFileDOC className="resource-item__icon" />
      <span className="resource-item__text">
        {file.typeCode === 'page_custom_form' ? (
          <span><strong>Online form:</strong> {cleanString(file.fileName)}</span>
        ) : (
          <span><strong>Visit page:</strong> {cleanString(file.fileName)}</span>
        )}
      </span>
    </a>
  );
};

function ResourceList(props) {
  const {
    dispatch,
    location,
    providerResourceFiles,
    tabPaneID,
  } = props;

  const getFiles = () => {
    dispatch(actions.fetchActions.getFiles(tabPaneID));
  };

  useEffect(() => {
    getFiles();
  }, [dispatch, tabPaneID]);

  return(
    providerResourceFiles.length ? (
      <div className="resource-list">
        {providerResourceFiles.map((file, fileIndex) => {
          const Item = file.typeCode === 'folder' ? ResourceListCollapse : ResourceItem;
          const itemProps = {};
          if (file.typeCode === 'folder') {
            // Auto-expand Ant Collapse component
            itemProps.selfExpandIndex = fileIndex + 1;
            itemProps.location = location;
          }
          return(
            <Item
              key={`resource-item-${fileIndex}`}
              file={file}
              {...itemProps}
            />
          );
        })}
      </div>
    ) : null
  );
}

const mapStateToProps = state => ({
  providerResourceFiles: state.providerResourceFiles.data,
});

export default connect(mapStateToProps)(withRouter(ResourceList));
